import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Plus, User, X, MessageSquare, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SearchBar } from '../Search/SearchBar';
import { CreatePostForm } from '../Communities/CreatePostForm';
import { CreateConversationPostForm } from '../Conversations/CreateConversationPostForm';
import { Avatar } from '../Profile/Avatar';
import { BottomSheet } from '../ui/BottomSheet';
import type { Community } from '../../types/communities';
import type { ConversationNiche } from '../../types/conversations';
import { NICHES } from '../../types/conversations';
import { useAuthProfile } from '../../contexts/AuthProfileContext';

type CreateMode = 'none' | 'selectNiche' | 'community' | 'conversation';

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, isAuthenticated } = useAuthProfile();
  const userId = user?.id ?? null;
  const username = profile?.username ?? null;
  const userAvatarUrl = profile?.avatar_url;
  const [showSearch, setShowSearch] = useState(false);
  const [showCommunitiesModal, setShowCommunitiesModal] = useState(false);
  const [userCommunities, setUserCommunities] = useState<Community[]>([]);
  const [selectedCommunity, setSelectedCommunity] = useState<Community | null>(null);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [createMode, setCreateMode] = useState<CreateMode>('none');
  const [selectedNiche, setSelectedNiche] = useState<ConversationNiche | null>(null);

  const fetchUserCommunities = async () => {
    if (!userId) return;

    try {
      const { data: communities, error } = await supabase
        .from('community_members')
        .select(`
          communities:community_id (
            id,
            name,
            description,
            type,
            avatar_url,
            banner_url
          )
        `)
        .eq('user_id', userId);

      if (error) throw error;

      const communitiesList = communities?.map(member => member.communities).filter(Boolean) as Community[] || [];
      setUserCommunities(communitiesList);
    } catch (error) {
      console.error('Error fetching user communities:', error);
    }
  };

  React.useEffect(() => {
    if (userId) {
      fetchUserCommunities();
    }
  }, [userId]);

  const handleProfileClick = (e: React.MouseEvent) => {
    if (!userId) {
      e.preventDefault();
      sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
      window.dispatchEvent(new CustomEvent('show-auth'));
    }
  };

  const handleMessageClick = (e: React.MouseEvent) => {
    if (!isAuthenticated) {
      e.preventDefault();
      sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
      window.dispatchEvent(new CustomEvent('show-auth'));
    } else {
      setShowSearch(false);
    }
  };

  const handleNavigationClick = () => {
    setShowSearch(false);
    setShowCommunitiesModal(false);
    setShowCreatePost(false);
    setCreateMode('none');
  };

  const handlePlusClick = (e: React.MouseEvent) => {
    if (!userId) {
      e.preventDefault();
      sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
      window.dispatchEvent(new CustomEvent('show-auth'));
      return;
    }
    // Go straight to conversation niche selection
    setCreateMode('selectNiche');
  };

  const handleNicheSelect = (niche: ConversationNiche) => {
    setSelectedNiche(niche);
    setCreateMode('conversation');
  };

  const handleCommunitySelect = (community: Community) => {
    setSelectedCommunity(community);
    setShowCommunitiesModal(false);
    setShowCreatePost(true);
  };

  const handlePostSuccess = (postId?: string) => {
    setShowCreatePost(false);
    setSelectedCommunity(null);
    setCreateMode('none');
    setSelectedNiche(null);

    if (postId) {
      navigate(`/communities/posts/${postId}`);
    }
  };

  const handleConversationPostSuccess = () => {
    setCreateMode('none');
    setSelectedNiche(null);
    navigate(`/conversations? niche=${selectedNiche}`);
  };

  const closeCreateModals = () => {
    setCreateMode('none');
    setShowCreatePost(false);
    setSelectedCommunity(null);
    setSelectedNiche(null);
  };

  return (
    <>
      {showSearch ? (
        <div className="fixed top-0 left-0 right-0 bottom-16 bg-white z-40 flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-accent-text/10">
            <h2 className="text-lg font-medium text-content">Search</h2>
            <button
              onClick={() => setShowSearch(false)}
              className="p-2 text-content/60 hover:text-content rounded-full hover:bg-accent-base/20"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <SearchBar onResultClick={() => setShowSearch(false)} />
          </div>
        </div>
      ) : null}

      {/* Full Screen Niche Selection */}
      {createMode === 'selectNiche' && (
        <div className="fixed inset-0 bg-white z-[9999] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-content">Choose a Topic</h2>
            <button
              onClick={closeCreateModals}
              className="p-2 text-content/60 hover:text-content rounded-full hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <p className="text-sm text-content/60 mb-4">Where do you want to post?</p>
            <div className="space-y-2">
              {NICHES.map((niche) => (
                <button
                  key={niche.id}
                  onClick={() => handleNicheSelect(niche.id)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 rounded-xl transition-colors text-left"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${niche.color.split(' ')[0]}`}>
                    {niche.icon}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-content">{niche.label}</p>
                    <p className="text-sm text-content/60 line-clamp-1">{niche.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create Conversation Post - Full Screen */}
      {createMode === 'conversation' && selectedNiche && (
        <div className="fixed inset-0 bg-white z-[9999] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-content">
              Post in {NICHES.find(n => n.id === selectedNiche)?.label}
            </h2>
            <button
              onClick={closeCreateModals}
              className="p-2 text-content/60 hover:text-content rounded-full hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <CreateConversationPostForm
              niche={selectedNiche}
              nicheLabel={NICHES.find(n => n.id === selectedNiche)?.label || ''}
              onSuccess={handleConversationPostSuccess}
              userProfile={{
                username: username || undefined,
                avatar_url: userAvatarUrl
              }}
            />
          </div>
        </div>
      )}

      {/* Bottom Navigation - Always visible */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-accent-text/10 lg:hidden z-50 shadow-lg fixed-bottom mobile-bottom-nav-safe">
        <nav className="flex items-center justify-around h-16">
          <Link
            to="/"
            onClick={handleNavigationClick}
            aria-label="Home"
            className={`flex items-center justify-center w-12 h-12 rounded-full active:scale-90 transition-transform duration-150 ${
              location.pathname === '/' ? 'text-accent-text' : 'text-content/60'
            }`}
          >
            <Home className="h-6 w-6" />
          </Link>

          <button
            onClick={handlePlusClick}
            aria-label="Create Post"
            className="flex items-center justify-center w-12 h-12 rounded-full text-content/60 active:scale-90 transition-transform duration-150"
          >
            <Plus className="h-6 w-6" />
          </button>

          <Link
            to="/near-me"
            onClick={handleNavigationClick}
            aria-label="Near Me"
            className={`flex items-center justify-center w-12 h-12 rounded-full active:scale-90 transition-transform duration-150 ${
              location.pathname === '/near-me' ? 'text-accent-text' : 'text-content/60'
            }`}
          >
            <MapPin className="h-6 w-6" />
          </Link>

          <Link
            to="/chat"
            onClick={handleMessageClick}
            aria-label="Messages"
            className={`flex items-center justify-center w-12 h-12 rounded-full active:scale-90 transition-transform duration-150 ${
              location.pathname.includes('/chat') ? 'text-accent-text' : 'text-content/60'
            }`}
          >
            <MessageSquare className="h-6 w-6" />
          </Link>

          <Link
            to={username ? `/profile/${username}` : '#'}
            onClick={(e) => {
              handleProfileClick(e);
              handleNavigationClick();
            }}
            aria-label="Profile"
            className={`flex items-center justify-center w-12 h-12 rounded-full ${
              location.pathname.includes('/profile') ? 'text-accent-text' : 'text-content/60'
            }`}
          >
            {isAuthenticated && userId && username ? (
              <Avatar userId={userId} username={username} url={userAvatarUrl} size="sm" disableLink={true} />
            ) : (
              <User className="h-6 w-6" />
            )}
          </Link>
        </nav>
      </div>

      {/* Communities Selection - Bottom Sheet */}
      <BottomSheet
        isOpen={showCommunitiesModal}
        onClose={() => setShowCommunitiesModal(false)}
        title="Select Community"
        maxHeight={70}
      >
        <div className="p-4">
          <div className="space-y-2">
            {userCommunities.length === 0 ? (
              <div className="text-center py-8 text-content/60">
                <p>You haven't joined any communities yet.</p>
                <Link
                  to="/communities"
                  className="text-accent-text hover:text-accent-text/80 mt-2 inline-block"
                  onClick={() => setShowCommunitiesModal(false)}
                >
                  Browse Communities
                </Link>
              </div>
            ) : (
              userCommunities.map((community) => (
                <button
                  key={community.id}
                  onClick={() => handleCommunitySelect(community)}
                  className="w-full p-4 text-left rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {community.avatar_url ? (
                      <img
                        src={community.avatar_url}
                        alt={community.name}
                        className="w-11 h-11 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-accent-base/20 flex items-center justify-center">
                        <span className="text-accent-text font-medium text-sm">
                          {community.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium text-content">{community.name}</h3>
                      {community.description && (
                        <p className="text-sm text-content/60 mt-0.5 line-clamp-1">
                          {community.description}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </BottomSheet>

      {/* Create Post in Community - Full Page */}
      {showCreatePost && selectedCommunity && (
        <div className="fixed inset-0 bg-white z-[9999] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-accent-text/10">
            <h2 className="text-lg font-medium text-content">Post to {selectedCommunity.name}</h2>
            <button
              onClick={() => setShowCreatePost(false)}
              className="p-2 text-content/60 hover:text-content rounded-full hover:bg-accent-base/20"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <CreatePostForm
              communityId={selectedCommunity.id}
              onSuccess={handlePostSuccess}
            />
          </div>
        </div>
      )}
    </>
  );
}
